import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { OrdersIcon, TicketIcon } from "./sidebar/Sidebarsvg";
import { useAuth } from "../../features/auth/AuthContext";
import { P } from "../../utils/permissions";
import { Routes as AppRoutes } from "../../routes";
import SidebarLogo from "../../images/header-logo-captify.svg";
import { LogoutSvg } from "../../svgFiles/LogoutSvg";
import UserAvatar from "./UserAvatar";
import { isAdminUser } from "../../helpers/adminAccess";
import { hasRole } from "../../helpers/roleNames";
import {
  setPage,
  resetFilters,
  updateOrdersFilterSearch,
} from "../../app/orderApi/filterSlice";
import {
  clearVaSidebarHospitalId,
  getVaSidebarHospitalId,
  setVaSidebarHospitalId,
} from "../../helpers/vaDistributorNavigation";
import { useGetVaHospitalsWithOrdersQuery } from "../../app/vaApi/vaApi";
import { useOrderSocket } from "../../hooks/useOrderSocket";
import "./adminSidebar.css";

const SIDEBAR_COLLAPSED_KEY = "adminSidebarCollapsed";
const SIDEBAR_WIDTH_KEY = "adminSidebarWidth";
const SIDEBAR_DEFAULT_WIDTH = 180;
const SIDEBAR_MIN_WIDTH = 160;
const SIDEBAR_MAX_WIDTH = 360;
const SIDEBAR_COLLAPSED_WIDTH = 64;

export function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeSidebarCollapsed(collapsed) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // ignore
  }
}

function readSidebarWidth() {
  try {
    const value = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
    if (Number.isFinite(value)) {
      return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, value));
    }
  } catch {
    // ignore
  }
  return SIDEBAR_DEFAULT_WIDTH;
}

function writeSidebarWidth(width) {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
  } catch {
    // ignore
  }
}

function clampSidebarWidth(width) {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(width)));
}

const SidebarToggleIcon = ({ collapsed }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    {collapsed ? (
      <path
        d="M6 3L11 8L6 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M10 3L5 8L10 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.5 2.5H11.5L12 4.1C12.2 4.3 12.5 4.4 12.8 4.6L14.3 3.8L16 6.1L14.6 7.1C14.7 7.4 14.7 7.7 14.7 8C14.7 8.3 14.7 8.6 14.6 8.9L16 9.9L14.3 12.2L12.8 11.4C12.5 11.6 12.2 11.7 12 11.9L11.5 13.5H8.5L8 11.9C7.8 11.7 7.5 11.6 7.2 11.4L5.7 12.2L4 9.9L5.4 8.9C5.3 8.6 5.3 8.3 5.3 8C5.3 7.7 5.3 7.4 5.4 7.1L4 6.1L5.7 3.8L7.2 4.6C7.5 4.4 7.8 4.3 8 4.1L8.5 2.5Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M10 10.3C11.2691 10.3 12.3 9.26906 12.3 8C12.3 6.73094 11.2691 5.7 10 5.7C8.73094 5.7 7.7 6.73094 7.7 8C7.7 9.26906 8.73094 10.3 10 10.3Z"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </svg>
);

const AppVersionsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="4" y="2.5" width="12" height="15" rx="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M7 6H13M7 9.5H13M7 13H10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const TransactionsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.5 4.5H16.5V16.5H3.5V4.5Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M3.5 8H16.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M7 4.5V16.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const PeopleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.333 17.5V15.8333C13.333 14.9493 12.982 14.1014 12.357 13.4763C11.7319 12.8512 10.884 12.5 10 12.5H5.83333C4.94928 12.5 4.10143 12.8512 3.47631 13.4763C2.85119 14.1014 2.5 14.9493 2.5 15.8333V17.5M15 17.5V15.8333C14.9991 15.0948 14.7524 14.3773 14.2976 13.7979C13.8428 13.2185 13.2069 12.811 12.5 12.6458M10.8333 2.60417C11.5443 2.76841 12.1835 3.17612 12.6407 3.75729C13.0979 4.33846 13.3448 5.05887 13.3417 5.8C13.3385 6.54113 13.0855 7.25954 12.6231 7.83629C12.1607 8.41304 11.5178 8.81533 10.805 8.97333M8.33333 5.8C8.33333 7.63467 6.968 9.13333 5.16667 9.13333C3.36533 9.13333 2 7.63467 2 5.8C2 3.96533 3.36533 2.46667 5.16667 2.46667C6.968 2.46667 8.33333 3.96533 8.33333 5.8Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d={open ? "M3 8L6 5L9 8" : "M3 4.5L6 7.5L9 4.5"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function isOrdersSection(pathname) {
  return (
    pathname === AppRoutes.Orders.path ||
    pathname.startsWith("/orders/") ||
    pathname.startsWith("/admin/orders/")
  );
}

function isDistributorSection(pathname) {
  return (
    pathname === AppRoutes.VaDistributorHome.path ||
    pathname.startsWith("/orders/order-details")
  );
}

export default function AdminSidebar({ collapsed = false, onToggleCollapsed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const assignedHospitalId = useSelector((state) =>
    String(state.filters.search?.assignedHospitalId || "").trim()
  );
  const { user, roles, logout, hasAnyPermission, hasPermission } = useAuth();
  const isSuperAdmin = hasRole(roles, "super-admin");
  const isVaAdmin = hasRole(roles, "va-admin");
  const orderChannel = useSelector((state) =>
    String(state.filters.search?.channel || "").trim().toLowerCase()
  );
  const canManageRedemption = isAdminUser();
  const [sidebarWidth, setSidebarWidth] = useState(readSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [hospitalsOpen, setHospitalsOpen] = useState(false);
  const dragRef = useRef({ startX: 0, startWidth: SIDEBAR_DEFAULT_WIDTH });

  useEffect(() => {
    const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth;
    const shell = document.querySelector(".admin-shell-sidebar");
    if (shell) {
      shell.style.setProperty("--admin-sidebar-width", `${width}px`);
    }
    document.documentElement.style.setProperty("--admin-sidebar-width", `${width}px`);
  }, [collapsed, sidebarWidth]);

  useEffect(() => {
    if (!isResizing) {
      writeSidebarWidth(sidebarWidth);
    }
  }, [isResizing, sidebarWidth]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    document.body.classList.add("is-sidebar-resizing");

    const onMove = (event) => {
      const next = clampSidebarWidth(
        dragRef.current.startWidth + (event.clientX - dragRef.current.startX)
      );
      setSidebarWidth(next);
    };

    const onUp = () => {
      setIsResizing(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      document.body.classList.remove("is-sidebar-resizing");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [isResizing]);

  const handleResizePointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (collapsed) {
      onToggleCollapsed?.();
    }
    dragRef.current = {
      startX: event.clientX,
      startWidth: sidebarWidth,
    };
    setIsResizing(true);
  };

  const handleResizeDoubleClick = () => {
    setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
    writeSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
  };

  const currentWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth;

  const canViewOrders = hasAnyPermission(
    P.ORDERS_READ_ALL,
    P.ORDERS_READ_ASSIGNED,
    P.ORDERS_READ_OWN
  );
  const canManageDistributors = hasPermission(P.VA_DISTRIBUTOR_MANAGE);
  const canReadAllOrders = hasPermission(P.ORDERS_READ_ALL);
  const vaAdminOnly = isVaAdmin && !canReadAllOrders;
  const showVaOrdersTab = isVaAdmin || (isSuperAdmin && canReadAllOrders);
  const showVaSettings =
    !canReadAllOrders && hasPermission(P.ORDERS_READ_ASSIGNED);
  // Keep VA hospital sidebar + metrics in sync when admin creates/assigns orders.
  useOrderSocket({ enabled: showVaSettings });
  const ordersNavPath =
    canReadAllOrders || vaAdminOnly
      ? AppRoutes.Orders.path
      : AppRoutes.VaDistributorHome.path;
  const activeHospitalId = showVaSettings ? assignedHospitalId : "";
  const ordersNavActive = canReadAllOrders
    ? isOrdersSection(location.pathname) && orderChannel !== "va"
    : isDistributorSection(location.pathname) && !activeHospitalId;
  const vaOrdersNavActive =
    showVaOrdersTab &&
    isOrdersSection(location.pathname) &&
    (vaAdminOnly || orderChannel === "va");

  // Restore hospital filter from session (and migrate legacy ?hospitalId=).
  useEffect(() => {
    if (!showVaSettings) return;
    const fromUrl = String(searchParams.get("hospitalId") || "").trim();
    if (fromUrl) {
      setVaSidebarHospitalId(fromUrl);
      dispatch(updateOrdersFilterSearch({ assignedHospitalId: fromUrl }));
      setSearchParams({}, { replace: true });
      return;
    }
    const stored = getVaSidebarHospitalId();
    if (stored && !assignedHospitalId) {
      dispatch(updateOrdersFilterSearch({ assignedHospitalId: stored }));
    }
  }, [showVaSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: hospitalsData, isError: hospitalsError } =
    useGetVaHospitalsWithOrdersQuery(undefined, { skip: !showVaSettings });

  const hospitalChildren = useMemo(() => {
    if (!showVaSettings) return [];
    const list = Array.isArray(hospitalsData)
      ? hospitalsData
      : Array.isArray(hospitalsData?.hospitals)
        ? hospitalsData.hospitals
        : [];
    if (hospitalsError) return [];
    return list
      .map((hospital) => {
        const id = String(hospital.id || hospital._id || "").trim();
        if (!id) return null;
        return {
          id,
          label: hospital.name || "Unknown hospital",
          isActive:
            location.pathname === AppRoutes.VaDistributorHome.path &&
            activeHospitalId === id,
        };
      })
      .filter(Boolean);
  }, [
    showVaSettings,
    hospitalsData,
    hospitalsError,
    location.pathname,
    activeHospitalId,
  ]);

  // Keep Hospitals dropdown open when a hospital filter is active.
  useEffect(() => {
    if (activeHospitalId) {
      setHospitalsOpen(true);
    }
  }, [activeHospitalId]);

  const handleLogoClick = () => {
    dispatch(setPage(1));
    dispatch(resetFilters());
    clearVaSidebarHospitalId();
    window.location.assign(ordersNavPath);
  };

  const handleOrdersClick = (e) => {
    dispatch(setPage(1));
    dispatch(updateOrdersFilterSearch({ channel: undefined }));
    if (!showVaSettings) return;
    e.preventDefault();
    dispatch(resetFilters());
    clearVaSidebarHospitalId();
    setHospitalsOpen(false);
    navigate(AppRoutes.VaDistributorHome.path);
  };

  const handleVaOrdersClick = (e) => {
    e.preventDefault();
    dispatch(setPage(1));
    dispatch(resetFilters());
    dispatch(updateOrdersFilterSearch({ channel: "va" }));
    clearVaSidebarHospitalId();
    navigate({ pathname: AppRoutes.Orders.path, search: "" });
  };

  const handleHospitalClick = (e, hospitalId) => {
    e.preventDefault();
    const id = String(hospitalId || "").trim();
    if (!id) return;
    dispatch(setPage(1));
    setVaSidebarHospitalId(id);
    dispatch(updateOrdersFilterSearch({ assignedHospitalId: id }));
    setHospitalsOpen(true);
    if (location.pathname !== AppRoutes.VaDistributorHome.path) {
      navigate(AppRoutes.VaDistributorHome.path);
    } else if (searchParams.get("hospitalId")) {
      setSearchParams({}, { replace: true });
    }
  };

  const navItems = [
    canViewOrders && {
      key: "orders",
      label: showVaSettings ? "All Orders" : "All orders",
      path: ordersNavPath,
      icon: <OrdersIcon />,
      isActive: ordersNavActive,
      onClick: handleOrdersClick,
    },
    showVaOrdersTab && {
      key: "va-orders",
      label: "VA orders",
      path: AppRoutes.Orders.path,
      icon: <OrdersIcon />,
      isActive: vaOrdersNavActive,
      onClick: handleVaOrdersClick,
    },
    canManageRedemption && {
      key: "redemption",
      label: "Redemption Code",
      path: AppRoutes.RedemptionCodes.path,
      icon: <TicketIcon />,
      isActive: location.pathname === AppRoutes.RedemptionCodes.path,
    },
    canManageRedemption && {
      key: "app-versions",
      label: "App Versions",
      path: AppRoutes.AppVersions.path,
      icon: <AppVersionsIcon />,
      isActive: location.pathname === AppRoutes.AppVersions.path,
    },
    (showVaSettings || vaAdminOnly) && {
      key: "settings",
      label: "Settings",
      path: AppRoutes.VaDistributorSettings.path,
      icon: <SettingsIcon />,
      isActive: location.pathname === AppRoutes.VaDistributorSettings.path,
    },
    showVaSettings && {
      key: "transactions",
      label: "Transactions",
      path: AppRoutes.VaDistributorTransactions.path,
      icon: <TransactionsIcon />,
      isActive: location.pathname === AppRoutes.VaDistributorTransactions.path,
    },
    canManageDistributors && {
      key: "va-distributors",
      label: "VA Distributors",
      path: AppRoutes.VaDistributorsAdmin.path,
      icon: <PeopleIcon />,
      isActive:
        location.pathname === AppRoutes.VaDistributorsAdmin.path ||
        location.pathname.startsWith(`${AppRoutes.VaDistributorsAdmin.path}/`),
    },
  ].filter(Boolean);

  return (
    <aside
      className={`admin-side_bar d-flex flex-column${
        collapsed ? " admin-side_bar--collapsed" : ""
      }${isResizing ? " admin-side_bar--resizing" : ""}`}
      style={{
        width: currentWidth,
        minWidth: currentWidth,
        maxWidth: currentWidth,
      }}
    >
      <div
        className="admin-sidebar-resize"
        onPointerDown={handleResizePointerDown}
        onDoubleClick={handleResizeDoubleClick}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        title="Drag to resize"
      />
      <div className="admin-sidebar-brand pt- pb-3">
        <div className="admin-sidebar-brand__row">
          {!collapsed ? (
            <button
              type="button"
              className="admin-sidebar-brand__btn"
              onClick={handleLogoClick}
              aria-label="Go to home"
            >
              <img src={SidebarLogo} alt="Captify logo" height={22} />
            </button>
          ) : null}
          <button
            type="button"
            className="admin-sidebar-toggle"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <SidebarToggleIcon collapsed={collapsed} />
          </button>
        </div>
      </div>

      <div
        className="admin-sidebar-user"
        title={collapsed ? user?.name || "User" : undefined}
      >
        <UserAvatar src={user?.picture} size={28} />
        {!collapsed ? (
          <div className="admin-sidebar-user__meta">
            <div className="admin-sidebar-user__name">
              {user?.name || "User"}
            </div>
            <div className="admin-sidebar-user__email">{user?.email}</div>
          </div>
        ) : null}
      </div>

      <nav className="admin-sidebar-nav" aria-label="Main">
        {!collapsed ? <p className="admin-sidebar-nav__label">Menu</p> : null}
        <ul className="admin-sidebar-nav__list mb-0">
          {navItems.map((item) => {
            const hasOrdersDropdown =
              item.key === "orders" &&
              showVaSettings &&
              !collapsed &&
              hospitalChildren.length > 0;

            return (
              <li
                key={item.key || item.path}
                className={`admin-sidebar-nav__item${
                  item.isActive && !hasOrdersDropdown ? " activeLink" : ""
                }${hasOrdersDropdown ? " admin-sidebar-nav__item--dropdown" : ""}`}
              >
                {hasOrdersDropdown ? (
                  <div
                    className={`admin-sidebar-nav__orders-row${
                      item.isActive ? " is-all-active" : ""
                    }${hospitalsOpen ? " is-open" : ""}${
                      activeHospitalId ? " has-hospital-active" : ""
                    }`}
                  >
                    <Link
                      to={{ pathname: item.path, search: "" }}
                      className="admin-sidebar-nav__link admin-sidebar-nav__link--orders"
                      title={item.label}
                      onClick={item.onClick}
                    >
                      <span
                        className="admin-sidebar-nav__icon"
                        aria-hidden="true"
                      >
                        {item.icon}
                      </span>
                      <span className="admin-sidebar-nav__text">
                        {item.label}
                      </span>
                    </Link>
                    <button
                      type="button"
                      className="admin-sidebar-nav__orders-chevron"
                      aria-expanded={hospitalsOpen}
                      aria-label={
                        hospitalsOpen ? "Hide hospitals" : "Show hospitals"
                      }
                      onClick={() => setHospitalsOpen((open) => !open)}
                    >
                      <ChevronIcon open={hospitalsOpen} />
                    </button>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className="admin-sidebar-nav__link"
                    title={collapsed ? item.label : undefined}
                    onClick={item.onClick}
                  >
                    <span
                      className="admin-sidebar-nav__icon"
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                    {!collapsed ? (
                      <span className="admin-sidebar-nav__text">
                        {item.label}
                      </span>
                    ) : (
                      <span className="visually-hidden">{item.label}</span>
                    )}
                  </Link>
                )}

                {hasOrdersDropdown && hospitalsOpen ? (
                  <ul className="admin-sidebar-nav__sublist">
                    {hospitalChildren.map((child) => (
                      <li
                        key={child.id}
                        className={`admin-sidebar-nav__subitem${
                          child.isActive ? " is-active" : ""
                        }`}
                      >
                        <Link
                          to={AppRoutes.VaDistributorHome.path}
                          className="admin-sidebar-nav__sublink"
                          title={child.label}
                          onClick={(e) => handleHospitalClick(e, child.id)}
                        >
                          <span className="admin-sidebar-nav__subtext">
                            {child.label}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="admin-sidebar-footer mt-auto">
        <div className={`admin-sidebar-materials${collapsed ? " is-collapsed" : ""}`}>
          {!collapsed && <>
            <h2>Electronic Materials</h2>
            <p>Download user guides, audiologist resources, and other materials.</p>
          </>}
          <a
            className="admin-sidebar-materials__link"
            href="https://drive.google.com/drive/folders/1lOyV5ngyiIt4mYoEsGW8YL-oANg5TUVI?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View electronic materials (opens in a new tab)"
            title={collapsed ? "Electronic Materials" : undefined}
          >
            {collapsed ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z"/><path d="M14 3v6h6M8 13h8M8 17h5"/></svg> : <>View Materials <span aria-hidden="true">→</span></>}
          </a>
        </div>
        <button
          type="button"
          className="admin-sidebar-logout"
          onClick={logout}
          title={collapsed ? "Logout" : undefined}
        >
          <LogoutSvg />
          {!collapsed ? (
            <span>Logout</span>
          ) : (
            <span className="visually-hidden">Logout</span>
          )}
        </button>
      </div>
    </aside>
  );
}
